<template>
  <article
    v-for="item in items"
    :key="`${item.id}_list`"
    class="content-wrapper"
  >
    <BaseImage class="image" :image="item.image" />
    <div class="date t-m-130 t-bold" v-html="nbsp(getFormattedDate(item))" />
    <div class="content">
      <span class="title t-h5" v-html="nbsp(item.course.title)" />
      <div class="t-p-s-130 t-bold name" v-html="nbsp(item.course.name)" />
      <span class="t-p-s-130 t-color-3 level"
        >{{ t('timetable.level') }}:
        {{ t(`timetable.predefined-filter.${item.course.course_level}`) }} -
        {{ t(`timetable.predefined-filter.${item.course.participant_type}`) }}
      </span>
      <div class="divider margin-auto" />
      <div class="coach">
        <span class="t-p-s-130">{{ t('timetable.teacher') }}: </span>
        <span class="t-p-s-130 t-bold">{{
          getTeachersFullnames(item.teachers)
        }}</span>
      </div>
      <div class="location">
        <span class="t-p-s-130">{{ t('timetable.location') }}: </span>
        <span class="t-p-s-130 t-bold">{{ item.city }}</span>
      </div>
      <div class="divider" />
      <div class="buttons-wrapper">
        <BaseButton
          tag="a"
          :href="item.register_url"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          :text="t('subscribe')"
        />
        <BaseButton
          :tag="NuxtLink"
          :to="
            useLocalePath()({
              name: 'course-slug-date',
              params: getSlugForCourseDate(item.full_slug)
            })
          "
          class="read-more-button"
          color="primary-grey"
          :text="t('read-more')"
        />
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import uniq from 'lodash/fp/uniq'

import { NuxtLink } from '#components'
import type { PublicCourseAndEventDatePage } from '~/service/__generated-api'

const { t } = useI18n()

defineProps<{
  items: PublicCourseAndEventDatePage[]
}>()

const getFormattedDate = (item: PublicCourseAndEventDatePage) => {
  const [startDate, endDate] = uniq([item.start_date, item.end_date])
  if (!endDate) return formatDate(startDate, 'DD.MM.YYYY')

  return `${formatDate(startDate, 'D.MM')}-${formatDate(endDate, 'DD.MM.YYYY')}`
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  @include flex-col;
  position: relative;
  justify-content: space-between;
  width: 100%;
  padding: rem(8px);
  border-radius: $border-radius;
}

.image {
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  border-radius: $border-radius-small;

  &::after {
    position: absolute;
    inset: 0;
    content: '';
    background: $image-gradient;
  }
}

.content {
  @include flex-col;
  z-index: 1;
  flex-grow: 1;
  padding: rem(20px) rem(18px) rem(18px) rem(18px);
  background-color: $layout-background-02;
  border-radius: $border-radius-small;
}

.date {
  z-index: 1;
  padding: rem(8px) rem(12px);
  margin-bottom: rem(195px);
  margin-left: auto;
  background-color: $layout-background-02;
  border-radius: $border-radius-small;
}

.divider {
  width: 100%;
  height: rem(1px);
  margin-block: rem(10px);
  background-color: $layout-divider;
}

.level {
  margin-bottom: rem(10px);
}

.margin-auto {
  margin-top: auto;
}

.buttons-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;

  :deep(.button) {
    max-width: rem(115px);
    text-align: center;
  }
}

.title {
  margin-bottom: rem(5px);
}

.name {
  flex: 1;
  min-height: rem(18px);
}

.location {
  margin-bottom: rem(2px);
}

.read-more-button :deep(.text) {
  @include capitalize;
}
</style>
